import React, { useEffect, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';
import { PrototypeScreen } from '../types/homepage';
interface ScreenLightboxProps {
  screen: PrototypeScreen | null;
  title: string;
  returnFocusTo: HTMLElement | null;
  onClose: () => void;
}
const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';
export const ScreenLightbox: React.FC<ScreenLightboxProps> = ({
  screen,
  title,
  returnFocusTo,
  onClose
}) => {
  const isOpen = screen !== null;
  const [shown, setShown] = useState<{
    screen: PrototypeScreen;
    title: string;
  } | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (screen) {
      returnFocusRef.current = returnFocusTo;
      setShown({
        screen,
        title
      });
      document.body.style.overflow = 'hidden';
      let secondFrame = 0;
      const firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => setIsAnimating(true));
      });
      return () => {
        cancelAnimationFrame(firstFrame);
        cancelAnimationFrame(secondFrame);
      };
    }
    setIsAnimating(false);
    document.body.style.overflow = '';
    if (returnFocusRef.current) {
      returnFocusRef.current.focus();
      returnFocusRef.current = null;
    }
    const timer = setTimeout(() => setShown(null), 300);
    return () => clearTimeout(timer);
  }, [screen, title, returnFocusTo]);
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  useEffect(() => {
    if (isOpen && shown) closeButtonRef.current?.focus();
  }, [isOpen, shown]);
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (focusables.length === 1) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);
  if (!shown) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-181" data-mp="3dz2rx51a8i7">
      <div aria-hidden="true" onClick={onClose} className={`absolute inset-0 bg-ink-text/60 backdrop-blur-sm transition-opacity duration-300 ease-out ${isAnimating ? 'opacity-100' : 'opacity-0'}`} data-id="element-182" data-mp="2j5mv1f93rcuo" />
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="screen-lightbox-title" aria-describedby="screen-lightbox-caption" className={`relative flex max-h-full flex-col items-center rounded-2xl bg-white p-4 shadow-card-hover transition-[opacity,transform] duration-300 ease-out sm:p-6 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`} data-id="element-183" data-mp="2fmxmazkdozwi">
        <div className="mb-4 flex w-full items-start justify-between gap-4" data-id="element-184" data-mp="1rz69zvyu8mv8">
          <div data-id="element-185" data-mp="1rx39gi027bc9">
            <h2 id="screen-lightbox-title" className="text-lg" data-id="element-186" data-mp="26o2ddfpy9bpi">
              {shown.title}
            </h2>
            <p id="screen-lightbox-caption" className="mt-1 text-sm text-plum-gray" data-id="element-187" data-mp="21xm0bwq1js3o">
              {shown.screen.caption}
            </p>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close enlarged screen" className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-cloud transition-colors duration-200 hover:bg-sandstone" data-id="element-188" data-mp="1e66v9w6vqwoq">
            <XIcon size={20} className="text-deep-juniper" aria-hidden="true" data-id="element-189" data-mp="hea617k8uvql" />
          </button>
        </div>
        <img src={shown.screen.src} alt={shown.screen.alt} className="h-auto max-h-[calc(100vh-10rem)] w-auto max-w-[calc(100vw-4rem)] rounded-[20px] border border-mist-sage object-contain sm:max-w-[calc(100vw-6rem)]" data-id="element-190" data-mp="lg56k9ucp53b" />
      </div>
    </div>;
};